import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  DestroyRef,
  ElementRef,
  NgZone,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  untracked,
  viewChild,
} from '@angular/core';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { StoryblokComponentInstance } from '../../../interfaces/storyblok';
import { preloadImage } from '../../../lib/preload-image';
import { PICFLOW_CONFIG } from '../../../picflow-config';
import { LanguageService } from '../../../services/language.service';
import { StoryblokLoadComponentsService } from '../../../services/storyblok-load-components.service';
import { convertToStoryblokCdn } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokImage } from '../../storyblok-types';
import { StoryblokDirective } from '../../storyblok.directive';
import {
  FALLBACK_VIDEO_TYPE,
  OPTIMIZED_VIDEO_TYPE,
  StoryblokVideoItem,
  getResponsiveVideoItemAsset,
  getVideoItem,
  getVideoItemPosterUrl,
} from '../video-item/video-item';

export type HeroGridType = 'none' | 'lines' | 'lines_vertical' | 'dots';

const DEFAULT_GRID_COLOR = '#1f232e';
const DEFAULT_GRID_OPACITY = 10;

/** One grid line during the entrance animation. */
interface GridLineEl {
  axis: 'v' | 'h';
  /** Cross-axis position (x for vertical, y for horizontal), CSS px. */
  pos: number;
  /** Entrance stagger, seconds. */
  delay: number;
  dur: number;
}

/** One grid dot during the entrance animation. */
interface GridDotEl {
  x: number;
  y: number;
  delay: number;
  dur: number;
}

// ── Line entrance choreography ────────────────────────────────────────────
// Vertical lines draw downwards from the top while fading in, as one simple
// center-out ripple: line 0 first, then pairs ±1, ±2, ±3, … outwards.
// Horizontal lines start when vertical pair ±3 does and ripple down from the
// top, each growing from the middle outwards with soft (feathered) tips.

/** Seconds between successive center-out vertical pairs. */
const PAIR_GAP = 0.1;
/** Sweep/fade duration of a single line. */
const LINE_DUR = 1.2;
/** Horizontals start together with vertical pair ±3. */
const H_OFFSET = 3 * PAIR_GAP;
/** Seconds between successive horizontal lines (top to bottom). */
const H_STEP = 0.08;
/**
 * Dots ripple out from the top center as a circular wave. Delays are
 * normalized to the farthest dot, so the wave always takes this many seconds
 * to reach the last ring — independent of section size / dot count.
 */
const DOT_RIPPLE_TOTAL = 1.2;
/** Pop-in duration of a single dot. */
const DOT_DUR = 0.5;

function easeOutCubic(p: number): number {
  const q = 1 - p;
  return 1 - q * q * q;
}

/** Ease with a slight overshoot — dots pop past their size and settle. */
function easeOutBack(p: number): number {
  const c = 1.70158;
  const q = p - 1;
  return 1 + (c + 1) * q * q * q + c * q * q;
}

/**
 * Hero: default. Derived from hero-tiles-floating but without the floating
 * tile field — instead an optional background grid (line grid or dotted
 * grid) drawn with pure CSS gradients, so it is SSR-safe and costs nothing
 * at runtime. Grid thickness, spacing and color (incl. opacity via rgba)
 * are editor-driven.
 *
 * The center copy renders nested Storyblok bloks (`content`) via ndc-dynamic;
 * blur wash and bottom video overlay are wired identically to
 * hero-tiles-floating.
 */
@Component({
  selector: 'app-hero-default',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hero-default.component.html',
  styleUrl: './hero-default.component.scss',
  imports: [StoryblokDirective, DynamicComponent, DynamicIoDirective],
})
export class HeroDefaultComponent implements OnInit {
  private storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);
  private config = inject(PICFLOW_CONFIG);
  private languageService = inject(LanguageService);
  private document = inject(DOCUMENT);

  readonly _editable = input<string>(undefined);
  readonly _uid = input<string>(undefined); // just here to prevent errors
  readonly component = input<string>(undefined); // just here to prevent errors

  /** Center copy bloks (Title / Text / Button / …), rendered above the grid. */
  readonly content = input<StoryblokComponentInstance[]>(undefined);

  /** Section background. Any CSS color; empty → transparent (inherits the page). */
  readonly backgroundColor = input<string>('');

  readonly theme = input<'light' | 'dark'>('light');

  /**
   * Soft colour wash behind the copy — a blur SVG picked from the asset
   * library, same as the page-level Top Blur on storyblok-root. Empty = no
   * blur.
   */
  readonly blur = input<StoryblokImage>(undefined);

  /**
   * Vertical placement of the blur blobs: "center" keeps them centered in
   * the upper stage, "top" shifts them up so they hug the top edge
   * (hero_tiles_floating look).
   */
  readonly blurPosition = input<'center' | 'top'>('center');

  /** CDN url of the picked blur asset; null hides the blur layer. */
  protected readonly blurSrc = computed(() => {
    const filename = this.blur()?.filename;
    return filename ? convertToStoryblokCdn(filename, this.config.storyblokUrl) : null;
  });

  // ── Background grid ───────────────────────────────────────────────────────

  /** Grid style: square line grid, vertical lines only, dots, or none. */
  readonly gridType = input<HeroGridType>('lines');

  /**
   * Grid stacking relative to the background gradient. "above" (default)
   * draws the grid over the gradient; "below" puts it underneath, so a
   * gradient that ends opaque fades the grid out (e.g. towards the bottom).
   */
  readonly gridPosition = input<'above' | 'below'>('above');

  /**
   * Section layout. "vertical_hug" (default): media below the copy, height
   * defined by the content. "vertical_90vh": same stacking, but the stage
   * spans ~90 % of the viewport height with the copy centered inside it.
   * Horizontal variants (media beside the copy) can join this list later.
   */
  readonly layout = input<'vertical_hug' | 'vertical_90vh'>('vertical_hug');

  /** Line width / dot radius, px. Storyblok number fields arrive as strings. */
  readonly gridThickness = input<number | string>(1);

  /** Cell size (spacing between lines / dots), px. */
  readonly gridDistance = input<number | string>(40);

  /**
   * Grid color. Prefer a solid color and control transparency via
   * gridOpacity — alpha in the color itself doubles up where lines cross.
   */
  readonly gridColor = input<string>(DEFAULT_GRID_COLOR);

  /**
   * Opacity of the whole grid layer, 0–100 %. Applied to the layer div, so
   * line intersections stay uniform instead of stacking to double opacity.
   */
  readonly gridOpacity = input<number | string>(DEFAULT_GRID_OPACITY);

  // ── Bottom video overlay — same wiring as hero-tiles-floating ─────────────

  readonly showVideo = input<boolean>(false);
  /** Section-wide background gradient, painted over the background color. */
  readonly backgroundGradient = input<string>('');

  /**
   * Height of the background gradient as % of the full section, ±1–100.
   * Positive values anchor the gradient to the top (50 = top half),
   * negative ones to the bottom (-50 = bottom half). Lets a pasted CSS
   * gradient cover part of the section without modifying the gradient.
   */
  readonly backgroundGradientHeight = input<number | string>(100);
  readonly videoItem = input<StoryblokVideoItem[]>(undefined);

  /** Registry of loaded Storyblok components for ndc-dynamic. */
  readonly components = this.storyblokLoadComponentsService.components;

  private readonly thickness = computed(() => Math.max(1, Number(this.gridThickness()) || 1));

  private readonly distance = computed(() => {
    // Cell must be larger than the stroke, otherwise the grid paints solid.
    const t = this.thickness();
    return Math.max(t + 2, Number(this.gridDistance()) || 40);
  });

  private readonly color = computed(() => this.gridColor()?.trim() || DEFAULT_GRID_COLOR);

  /** Layer opacity 0–1, from the 0–100 % editor value. */
  protected readonly gridLayerOpacity = computed(() => {
    const raw = Number(this.gridOpacity());
    const pct = Number.isFinite(raw) ? raw : DEFAULT_GRID_OPACITY;
    return Math.min(100, Math.max(0, pct)) / 100;
  });

  // ── Grid rendering (canvas) ─────────────────────────────────────────────────
  //
  // The grid is drawn on a canvas so every element can enter on its own during
  // the entrance choreography. Once the last element has settled the rAF loop
  // stops — the canvas is then a static picture. Reduced motion skips straight
  // to the settled end state.

  private readonly gridCanvasRef = viewChild<ElementRef<HTMLCanvasElement>>('gridCanvas');
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  private gridLines: GridLineEl[] = [];
  private gridDots: GridDotEl[] = [];
  private gridCssWidth = 0;
  private gridCssHeight = 0;
  private gridRafId = 0;
  private gridTotalDur = 0;
  /** True once the entrance has fully played (or reduced motion skipped it). */
  private gridSettled = false;
  private gridObserver: ResizeObserver | null = null;
  private reducedMotion = false;
  /** The canvas currently wired up (observer + animation). */
  private gridSetupCanvas: HTMLCanvasElement | null = null;
  /** Browser-only gate — effects also run during SSR, afterNextRender doesn't. */
  private browserReady = false;

  constructor() {
    afterNextRender(() => {
      this.browserReady = true;
      this.trySetupGridCanvas();
    });
    // The canvas renders whenever ndc-dynamic gets around to it — often after
    // afterNextRender has already fired — so setup must react to the element
    // appearing, not to a one-shot lifecycle hook.
    effect(() => {
      this.gridCanvasRef();
      untracked(() => this.trySetupGridCanvas());
    });
    // Restart the entrance when grid inputs change (live editing in Storyblok).
    effect(() => {
      this.gridType();
      this.gridThickness();
      this.gridDistance();
      this.gridColor();
      untracked(() => this.restartGridAnimation());
    });
    this.destroyRef.onDestroy(() => {
      if (this.gridRafId) cancelAnimationFrame(this.gridRafId);
      this.gridObserver?.disconnect();
    });
  }

  private trySetupGridCanvas(): void {
    if (!this.browserReady) return;
    const canvas = this.gridCanvasRef()?.nativeElement ?? null;
    if (canvas === this.gridSetupCanvas) return;

    // Canvas removed or replaced (e.g. Animate Grid toggled in the editor):
    // tear down the old wiring before setting up the new element.
    this.gridObserver?.disconnect();
    this.gridObserver = null;
    if (this.gridRafId) {
      cancelAnimationFrame(this.gridRafId);
      this.gridRafId = 0;
    }
    this.gridSetupCanvas = canvas;
    if (!canvas) return;

    this.setupGridCanvas(canvas);
  }

  private setupGridCanvas(canvas: HTMLCanvasElement): void {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.gridCssWidth = rect.width;
      this.gridCssHeight = rect.height;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.buildGridElements();
      // Mid-animation the loop repaints anyway. Only repaint the end state
      // for the new size once the entrance has fully played — otherwise the
      // full grid would flash before the animation starts.
      if (this.gridSettled) {
        this.drawGridFrame(Number.POSITIVE_INFINITY);
      } else if (!this.gridRafId && this.gridCssWidth) {
        // The canvas can measure 0×0 when the entrance is first kicked off
        // (layout not settled during hydration) — in that case the start is
        // aborted, and this first real measurement has to trigger it.
        this.restartGridAnimation();
      }
    };
    this.gridObserver = new ResizeObserver(resize);
    this.gridObserver.observe(canvas);
    resize();

    this.restartGridAnimation();
  }

  private restartGridAnimation(): void {
    const canvas = this.gridCanvasRef()?.nativeElement;
    if (!canvas || !this.gridCssWidth) return;

    this.buildGridElements();
    this.gridSettled = false;
    if (this.gridRafId) {
      cancelAnimationFrame(this.gridRafId);
      this.gridRafId = 0;
    }

    if (this.reducedMotion) {
      this.gridSettled = true;
      this.drawGridFrame(Number.POSITIVE_INFINITY);
      return;
    }

    // Accumulate clamped per-frame deltas instead of wall-clock time: a long
    // main-thread stall (hydration, lazy chunks) then pauses the entrance
    // instead of skipping most of it.
    let elapsed = 0;
    let lastFrame = 0;
    this.zone.runOutsideAngular(() => {
      const loop = (now: number): void => {
        if (lastFrame) elapsed += Math.min(now - lastFrame, 50) / 1000;
        lastFrame = now;
        this.drawGridFrame(elapsed);
        if (elapsed >= this.gridTotalDur) {
          this.gridRafId = 0;
          this.gridSettled = true;
          return;
        }
        this.gridRafId = requestAnimationFrame(loop);
      };
      this.gridRafId = requestAnimationFrame(loop);
    });
  }

  /** Rebuilds the per-element models for the current size / grid settings. */
  private buildGridElements(): void {
    const W = this.gridCssWidth;
    const H = this.gridCssHeight;
    const d = this.distance();
    const t = this.thickness();
    const type = this.gridType();
    this.gridLines = [];
    this.gridDots = [];
    if (!W || !H || type === 'none') return;

    // Same geometry as the CSS layer: vertical positions flank the center in
    // symmetric pairs offset half a cell — a full square (not a line) sits on
    // the section's center axis. Horizontal ones sit on full-cell boundaries
    // (d, 2d, …) so the top row is a perfect square — no line on the top edge.
    const kMax = Math.ceil((W / 2 + t) / d);
    const ys: number[] = [];
    for (let y = d; y < H + t; y += d) ys.push(y);

    if (type === 'lines' || type === 'lines_vertical') {
      for (let k = -kMax; k < kMax; k++) {
        const x = W / 2 + (k + 0.5) * d;
        if (x < -t || x > W + t) continue;
        // Center-out ripple by pair: innermost ±½ first, then ±1½, ±2½, …
        const pair = k >= 0 ? k : -1 - k;
        this.gridLines.push({ axis: 'v', pos: x, delay: pair * PAIR_GAP, dur: LINE_DUR });
      }
      if (type === 'lines') {
        // Horizontals ripple down from the top, starting with the 4th vertical pair.
        ys.forEach((y, i) => {
          this.gridLines.push({ axis: 'h', pos: y, delay: H_OFFSET + i * H_STEP, dur: LINE_DUR });
        });
      }
    } else {
      // Circular wave from the top center: delay grows with the distance
      // from (W/2, 0), so dots pop in along expanding rings. Delays are
      // scaled to the farthest dot so the wave duration stays constant no
      // matter how large the grid is.
      let maxDist = 0;
      for (let k = -kMax; k < kMax; k++) {
        const x = W / 2 + (k + 0.5) * d;
        if (x < -t || x > W + t) continue;
        for (const y of ys) {
          const dist = Math.hypot(x - W / 2, y);
          maxDist = Math.max(maxDist, dist);
          this.gridDots.push({ x, y, delay: dist, dur: DOT_DUR });
        }
      }
      if (maxDist > 0) {
        for (const dot of this.gridDots) dot.delay = (dot.delay / maxDist) * DOT_RIPPLE_TOTAL;
      }
    }

    let end = 0;
    for (const l of this.gridLines) end = Math.max(end, l.delay + l.dur);
    for (const p of this.gridDots) end = Math.max(end, p.delay + p.dur);
    this.gridTotalDur = end + 0.05;
  }

  /** Paints one frame; elapsed = Infinity paints the settled end state. */
  private drawGridFrame(elapsed: number): void {
    const canvas = this.gridCanvasRef()?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !this.gridCssWidth) return;

    const W = this.gridCssWidth;
    const H = this.gridCssHeight;
    const t = this.thickness();
    const color = this.color();
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = color;

    for (const line of this.gridLines) {
      const p = Math.min(1, Math.max(0, (elapsed - line.delay) / line.dur));
      if (p <= 0) continue;
      if (line.axis === 'v') this.drawSweepLine(ctx, line, p, H, t, color);
      else this.drawCenterGrowLine(ctx, line, p, W, t, color);
    }

    // A dot needs more footprint than a line of the same stroke to register
    // visually — scale the radius up from the thickness value.
    const dotRadius = Math.max(1.5, t * 1.5);
    for (const dot of this.gridDots) {
      const p = Math.min(1, Math.max(0, (elapsed - dot.delay) / dot.dur));
      if (p <= 0) continue;
      ctx.globalAlpha = easeOutCubic(p);
      const r = Math.max(0, dotRadius * easeOutBack(p));
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  }

  /**
   * Draws one vertical line sweeping down from the top while fading in.
   * The leading edge is a long feathered gradient (no hard tip) and the
   * whole line's opacity ramps with progress, so the line both draws and
   * fades. The head travels the line's length plus the feather, so at p = 1
   * the solid part covers the full line and the feather has slid off.
   */
  private drawSweepLine(
    ctx: CanvasRenderingContext2D,
    line: GridLineEl,
    p: number,
    H: number,
    t: number,
    color: string
  ): void {
    const feather = Math.min(320, H * 0.5);
    const head = easeOutCubic(p) * (H + feather);
    const drawn = Math.min(head, H);
    if (drawn <= 0) return;

    let fill: string | CanvasGradient = color;
    if (head < H + feather) {
      // Solid from the top, fading out over the last `feather` px of the
      // travelled distance.
      const gradient = ctx.createLinearGradient(0, 0, 0, head);
      gradient.addColorStop(0, color);
      gradient.addColorStop(Math.max(0, (head - feather) / head), color);
      gradient.addColorStop(1, 'transparent');
      fill = gradient;
    }

    ctx.globalAlpha = easeOutCubic(p);
    ctx.fillStyle = fill;
    ctx.fillRect(line.pos - t / 2, 0, t, drawn);
    ctx.fillStyle = color;
    ctx.globalAlpha = 1;
  }

  /**
   * Draws one horizontal line growing out of the horizontal center in both
   * directions while fading in, with feathered (soft) tips on both ends.
   * Each half travels half the width plus the feather, so at p = 1 the line
   * is fully solid edge to edge.
   */
  private drawCenterGrowLine(
    ctx: CanvasRenderingContext2D,
    line: GridLineEl,
    p: number,
    W: number,
    t: number,
    color: string
  ): void {
    const half = W / 2;
    const feather = Math.min(240, half * 0.5);
    const head = easeOutCubic(p) * (half + feather);
    const drawn = Math.min(head, half);
    if (drawn <= 0) return;

    let fill: string | CanvasGradient = color;
    if (head < half + feather) {
      // Symmetric gradient: soft tip on the left, solid center, soft tip on
      // the right.
      const gradient = ctx.createLinearGradient(half - head, 0, half + head, 0);
      const tip = feather / (2 * head);
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(Math.min(0.5, tip), color);
      gradient.addColorStop(Math.max(0.5, 1 - tip), color);
      gradient.addColorStop(1, 'transparent');
      fill = gradient;
    }

    ctx.globalAlpha = easeOutCubic(p);
    ctx.fillStyle = fill;
    ctx.fillRect(half - drawn, line.pos - t / 2, drawn * 2, t);
    ctx.fillStyle = color;
    ctx.globalAlpha = 1;
  }

  // ── Video — driven entirely by the shared video_item blok ──────────────────

  protected readonly resolvedVideoItem = computed(() => getVideoItem(this.videoItem()));
  protected readonly resolvedVideoWidth = computed(() => Number(this.resolvedVideoItem()?.width) || 1620);
  protected readonly resolvedVideoHeight = computed(() => Number(this.resolvedVideoItem()?.height) || 1080);

  /** Poster for the video — the LCP element of pages using this hero. */
  protected readonly videoPoster = computed(() =>
    getVideoItemPosterUrl(this.resolvedVideoItem(), this.config.storyblokUrl)
  );

  ngOnInit(): void {
    // High-priority head preload so the poster is fetched before Low-priority
    // image queue. crossorigin must match the <video> (anonymous), otherwise
    // the preloaded response has a different cache key and is downloaded twice.
    const poster = this.showVideo() ? this.videoPoster() : undefined;
    if (poster) {
      preloadImage(poster, this.document, { fetchpriority: 'high', crossorigin: 'anonymous' });
    }
  }

  protected readonly backgroundGradientImage = computed(() => {
    const gradient = this.backgroundGradient()?.trim().replace(/;+$/, '');
    if (!gradient) return null;
    return gradient;
  });

  /**
   * background-size for the gradient: full width, |X| % of the section
   * height. The gradient itself stays untouched — it's just painted over a
   * shorter area (no-repeat), and the rest shows the background color.
   */
  protected readonly backgroundGradientSize = computed(() => {
    const raw = Number(this.backgroundGradientHeight());
    const pct = Number.isFinite(raw) && raw !== 0 ? Math.min(100, Math.abs(raw)) : 100;
    return `100% ${pct}%`;
  });

  /** Positive height anchors the gradient to the top, negative to the bottom. */
  protected readonly backgroundGradientPosition = computed(() => {
    const raw = Number(this.backgroundGradientHeight());
    return raw < 0 ? '50% 100%' : '50% 0';
  });

  // Sources are selected natively via `<source media>` (viewport-based, at
  // load time) instead of the SSR UA sniff.
  protected readonly desktopSrc = computed(() => {
    const asset = getResponsiveVideoItemAsset(this.resolvedVideoItem(), false);
    return asset?.filename ? convertToStoryblokCdn(asset.filename, this.config.storyblokUrl) : undefined;
  });

  /** Undefined when no distinct mobile rendition is configured. */
  protected readonly mobileSrc = computed(() => {
    const asset = getResponsiveVideoItemAsset(this.resolvedVideoItem(), true);
    const src = asset?.filename ? convertToStoryblokCdn(asset.filename, this.config.storyblokUrl) : undefined;
    return src && src !== this.desktopSrc() ? src : undefined;
  });

  // Optimized (smaller codec) renditions — video_item only, no legacy inputs.
  // Rendered before the H.264 sources with an explicit `type`, so browsers
  // that can't decode the codec skip them without downloading.
  protected readonly optimizedType = OPTIMIZED_VIDEO_TYPE;
  protected readonly fallbackType = FALLBACK_VIDEO_TYPE;

  protected readonly desktopOptimizedSrc = computed(() => {
    const filename = this.resolvedVideoItem()?.videoOptimized?.filename;
    return filename ? convertToStoryblokCdn(filename, this.config.storyblokUrl) : undefined;
  });

  protected readonly mobileOptimizedSrc = computed(() => {
    const filename = this.resolvedVideoItem()?.mobileVideoOptimized?.filename;
    const src = filename ? convertToStoryblokCdn(filename, this.config.storyblokUrl) : undefined;
    return src && src !== this.desktopOptimizedSrc() ? src : undefined;
  });

  protected readonly srclang = computed(() => this.languageService.currentLanguage() || 'en');

  protected readonly tracks = computed(() => {
    const videoTrack = this.resolvedVideoItem()?.videoTrack;
    const src = this.desktopSrc();
    const srclang = this.srclang();
    if (!videoTrack || !src) return [];

    const validTracks = videoTrack
      .filter(t => t?.vtt?.filename)
      .map(t => ({
        ...t,
        src: convertToStoryblokCdn(t.vtt.filename, this.config.storyblokUrl),
      }));
    if (!validTracks.length) return [];

    return [
      ...validTracks,
      {
        _uid: 'video-track',
        kind: 'video' as const,
        src,
        srclang,
        default: true,
      },
    ];
  });
}
